if (!customElements.get("formula-video-reels")) {
  class FormulaVideoReels extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.sliderElement = this.querySelector("[data-reel-slider]");
      this.videos = Array.from(this.querySelectorAll(".formula-video-reels__video"));
      this.autoplayEnabled = this.dataset.autoplay === "true";
      this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.sectionVisible = false;
      this.manualVideo = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleMediaStateChange = this.handleMediaStateChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);

      this.addEventListener("click", this.handleClick);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
      document.addEventListener("shopify:block:select", this.handleBlockSelect);

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener("change", this.handleMotionChange);
      } else {
        this.motionQuery.addListener(this.handleMotionChange);
      }

      this.videos.forEach((video) => {
        video.muted = true;
        video.addEventListener("play", this.handleMediaStateChange);
        video.addEventListener("pause", this.handleMediaStateChange);
        video.addEventListener("volumechange", this.handleMediaStateChange);
        this.updateMediaControls(video);
      });

      this.initializeObserver();
      this.initializeSlider();
    }

    disconnectedCallback() {
      window.clearTimeout(this.sliderTimer);
      this.observer?.disconnect();
      this.slider?.destroy(true, false);
      this.pauseAllVideos();

      this.removeEventListener("click", this.handleClick);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
      document.removeEventListener("shopify:block:select", this.handleBlockSelect);

      if (this.motionQuery?.removeEventListener) {
        this.motionQuery.removeEventListener("change", this.handleMotionChange);
      } else {
        this.motionQuery?.removeListener(this.handleMotionChange);
      }

      this.videos?.forEach((video) => {
        video.removeEventListener("play", this.handleMediaStateChange);
        video.removeEventListener("pause", this.handleMediaStateChange);
        video.removeEventListener("volumechange", this.handleMediaStateChange);
      });

      this.slider = null;
      this.observer = null;
      this.initialized = false;
    }

    initializeSlider(attempt = 0) {
      if (!this.sliderElement) return;

      if (!window.FormulaLibs?.Swiper) {
        if (attempt < 30) {
          this.sliderTimer = window.setTimeout(() => this.initializeSlider(attempt + 1), 100);
        }
        return;
      }

      const previousButton = this.querySelector("[data-reel-previous]");
      const nextButton = this.querySelector("[data-reel-next]");
      const pagination = this.querySelector(".formula-video-reels__pagination");

      this.slider = new window.FormulaLibs.Swiper(this.sliderElement, {
        slidesPerView: 1.16,
        slidesPerGroup: 1,
        spaceBetween: 14,
        speed: 500,
        grabCursor: true,
        loop: false,
        watchOverflow: true,
        navigation: previousButton && nextButton
          ? {
              prevEl: previousButton,
              nextEl: nextButton,
            }
          : false,
        pagination: pagination
          ? {
              el: pagination,
              clickable: true,
            }
          : false,
        breakpoints: {
          560: {
            slidesPerView: 2.1,
            spaceBetween: 18,
          },
          768: {
            slidesPerView: 2.55,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3.15,
            spaceBetween: 24,
          },
          1280: {
            slidesPerView: 4,
            spaceBetween: 28,
          },
        },
        on: {
          init: () => this.syncPlayback(),
          slideChangeTransitionStart: () => {
            this.manualVideo = null;
            this.pauseAllVideos();
            this.muteAllVideos();
          },
          slideChangeTransitionEnd: () => this.syncPlayback(),
        },
      });
    }

    initializeObserver() {
      if (!("IntersectionObserver" in window)) {
        this.sectionVisible = true;
        this.syncPlayback();
        return;
      }

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = entry.isIntersecting && entry.intersectionRatio >= 0.1;

          if (!this.sectionVisible) {
            this.manualVideo = null;
            this.pauseAllVideos();
            this.muteAllVideos();
            return;
          }

          this.syncPlayback();
        },
        {
          threshold: [0, 0.1, 0.35],
        },
      );

      this.observer.observe(this);
    }

    handleClick(event) {
      const playButton = event.target.closest("[data-reel-play]");
      if (playButton && this.contains(playButton)) {
        const video = playButton.closest(".formula-video-reels__card")?.querySelector(".formula-video-reels__video");
        if (!video) return;

        if (video.paused) {
          this.manualVideo = video;
          video.dataset.userPaused = "false";
          this.pauseAllVideos(video);
          this.playVideo(video);
        } else {
          video.dataset.userPaused = "true";
          video.pause();
          this.manualVideo = null;
        }
        return;
      }

      const soundButton = event.target.closest("[data-reel-sound]");
      if (soundButton && this.contains(soundButton)) {
        const video = soundButton.closest(".formula-video-reels__card")?.querySelector(".formula-video-reels__video");
        if (!video) return;

        if (video.muted) {
          this.videos.forEach((item) => {
            if (item !== video) item.muted = true;
          });
          video.muted = false;
          this.manualVideo = video;
          this.pauseAllVideos(video);
          this.playVideo(video);
        } else {
          video.muted = true;
        }
      }
    }

    handleMediaStateChange(event) {
      this.updateMediaControls(event.currentTarget);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.manualVideo = null;
        this.pauseAllVideos();
        this.muteAllVideos();
      } else {
        this.syncPlayback();
      }
    }

    handleMotionChange() {
      this.manualVideo = null;
      this.pauseAllVideos();
      this.muteAllVideos();
      this.syncPlayback();
    }

    handleBlockSelect(event) {
      const slide = event.target.closest?.(".formula-video-reels__slide");
      if (!slide || !this.contains(slide)) return;

      const index = Number(slide.dataset.slideIndex);
      if (this.slider && Number.isFinite(index)) {
        this.slider.slideTo(index);
      }
    }

    syncPlayback() {
      if (!this.sectionVisible || document.hidden) {
        this.pauseAllVideos();
        return;
      }

      const activeVideo = this.manualVideo || this.querySelector(".swiper-slide-active .formula-video-reels__video") || this.videos[0];
      this.pauseAllVideos(activeVideo);

      if (
        !activeVideo ||
        !this.autoplayEnabled ||
        this.motionQuery.matches ||
        activeVideo.dataset.userPaused === "true"
      ) {
        return;
      }

      if (!this.manualVideo) {
        activeVideo.muted = true;
      }

      this.playVideo(activeVideo);
    }

    playVideo(video) {
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => this.updateMediaControls(video));
      }
    }

    pauseAllVideos(exceptVideo = null) {
      this.videos.forEach((video) => {
        if (video !== exceptVideo && !video.paused) {
          video.pause();
        }
      });
    }

    muteAllVideos(exceptVideo = null) {
      this.videos.forEach((video) => {
        if (video !== exceptVideo) {
          video.muted = true;
        }
      });
    }

    updateMediaControls(video) {
      const card = video.closest(".formula-video-reels__card");
      const playButton = card?.querySelector("[data-reel-play]");
      const soundButton = card?.querySelector("[data-reel-sound]");

      if (playButton) {
        playButton.classList.toggle("is-playing", !video.paused);
        playButton.setAttribute(
          "aria-label",
          video.paused ? playButton.dataset.labelPlay : playButton.dataset.labelPause,
        );
      }

      if (soundButton) {
        soundButton.classList.toggle("is-unmuted", !video.muted);
        soundButton.setAttribute(
          "aria-label",
          video.muted ? soundButton.dataset.labelUnmute : soundButton.dataset.labelMute,
        );
      }
    }
  }

  customElements.define("formula-video-reels", FormulaVideoReels);
}
